import { Packet } from "../../core/protocol/packets";
import { PacketTypes } from "../../core/protocol/types";
import { PingPacket, PongPacket } from "../../core/protocol/packets";
import { PacketFactory } from "../../core/protocol/PacketFactory";

type PacketHandler = (packet: Packet) => void;

export class GameClientSocket {
  private ws: WebSocket | null = null;
  private handlers: Map<number, PacketHandler[]> = new Map();
  private _url: string;
  private pingInterval: number | null = null;
  private latency: number = 0;

  public onConnect?: () => void;
  public onDisconnect?: () => void;
  public onError?: (err: Event) => void;

  constructor(url: string) {
    this._url = url;
    // Default handlers
    this.registerHandler(PacketTypes.PING, this.handlePing.bind(this));
    this.registerHandler(PacketTypes.PONG, this.handlePong.bind(this));
  }

  public get url(): string {
      return this._url;
  }

  public connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Check if we are already connected to the same URL
        if (this.ws && this.ws.readyState === WebSocket.OPEN && this.ws.url === this._url) {
            resolve();
            return;
        }

        this.ws = new WebSocket(this._url);
        this.ws.binaryType = "arraybuffer";

        this.ws.onopen = () => {
          console.log("[GameClientSocket] Connected to", this._url);
          this.startPing();
          this.onConnect?.();
          resolve();
        };

        this.ws.onclose = () => {
          console.log("[GameClientSocket] Disconnected");
          this.stopPing();
          this.onDisconnect?.();
        };

        this.ws.onerror = (err) => {
          console.error("[GameClientSocket] Error:", err);
          this.onError?.(err);
          if (this.ws?.readyState === WebSocket.CONNECTING) {
             reject(err);
          }
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event);
        };

      } catch (e) {
        reject(e);
      }
    });
  }

  public disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.stopPing();
  }

  public send(packet: Packet) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const payload = packet.serialize();
      const message = JSON.stringify({
        packetId: packet.packetId,
        data: payload
      });
      this.ws.send(message);
    } else {
      console.warn("[GameClientSocket] Cannot send, socket not open");
    }
  }

  public on(packetId: number, handler: PacketHandler) {
    this.registerHandler(packetId, handler);
  }

  public off(packetId: number, handler: PacketHandler) {
    const list = this.handlers.get(packetId);
    if (list) {
      this.handlers.set(packetId, list.filter(h => h !== handler));
    }
  }

  private registerHandler(packetId: number, handler: PacketHandler) {
      if (!this.handlers.has(packetId)) {
          this.handlers.set(packetId, []);
      }
      this.handlers.get(packetId)?.push(handler);
  }

  private handleMessage(event: MessageEvent) {
    try {
      let data = event.data;
      if (typeof data === "string") {
        const raw = JSON.parse(data);
        const { packetId, data: packetData } = raw;
        
        const handlers = this.handlers.get(packetId);
        if (handlers) {
          const packet = PacketFactory.create(packetId);
          if (packet) {
            packet.deserialize(packetData);
            packet.senderId = "server"; 
            handlers.forEach(h => h(packet));
          }
        }
      }
    } catch (e) {
      console.error("[GameClientSocket] Failed to process message", e);
    }
  }

  private startPing() {
    this.stopPing();
    // ping every 5s
    this.pingInterval = window.setInterval(() => {
      const ping = new PingPacket();
      ping.timestamp = Date.now();
      this.send(ping);
    }, 5000);
  }

  private stopPing() {
    if (this.pingInterval) {
      window.clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private handlePing(packet: Packet) {
    const ping = packet as PingPacket;
    const pong = new PongPacket();
    pong.originalTimestamp = ping.timestamp;
    pong.pingId = ping.pingId;
    this.send(pong);
  }

  private handlePong(packet: Packet) {
    const pong = packet as PongPacket;
    const now = Date.now();
    this.latency = (now - pong.originalTimestamp) / 2;
  }
}
